import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useCategoriaCampos } from '@/hooks/useCategoriaCampos';
import { CategoriaCampoCustomizado } from '@/types/categoria-campo';

interface DynamicCategoriaFieldsProps {
  form: UseFormReturn<any>;
  categoriaId?: string;
}

export const DynamicCategoriaFields = ({ form, categoriaId }: DynamicCategoriaFieldsProps) => {
  const { campos, isLoading } = useCategoriaCampos(categoriaId);

  if (!categoriaId || isLoading || campos.length === 0) {
    return null;
  }

  const renderField = (campo: CategoriaCampoCustomizado) => {
    const fieldName = `campos_customizados.${campo.nome_campo}`;

    switch (campo.tipo_campo) {
      case 'text':
      case 'email':
      case 'url':
        return (
          <FormField
            key={campo.id}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {campo.label}
                  {campo.obrigatorio && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    type={campo.tipo_campo === 'email' ? 'email' : campo.tipo_campo === 'url' ? 'url' : 'text'}
                    placeholder={campo.configuracoes?.placeholder}
                    {...field}
                  />
                </FormControl>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'textarea':
        return (
          <FormField
            key={campo.id}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {campo.label}
                  {campo.obrigatorio && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={campo.configuracoes?.placeholder}
                    {...field}
                  />
                </FormControl>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'number':
        return (
          <FormField
            key={campo.id}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {campo.label}
                  {campo.obrigatorio && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={campo.configuracoes?.min}
                    max={campo.configuracoes?.max}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  />
                </FormControl>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'date':
        return (
          <FormField
            key={campo.id}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {campo.label}
                  {campo.obrigatorio && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'boolean':
        return (
          <FormField
            key={campo.id}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {campo.label}
                    {campo.obrigatorio && <span className="text-destructive ml-1">*</span>}
                  </FormLabel>
                  {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                </div>
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />
        );

      case 'select':
        return (
          <FormField
            key={campo.id}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {campo.label}
                  {campo.obrigatorio && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma opção" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {campo.configuracoes?.opcoes?.map((opcao) => (
                      <SelectItem key={opcao} value={opcao}>
                        {opcao}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'multiselect':
        return (
          <FormField
            key={campo.id}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {campo.label}
                  {campo.obrigatorio && <span className="text-destructive ml-1">*</span>}
                </FormLabel>
                <div className="space-y-2">
                  {campo.configuracoes?.opcoes?.map((opcao) => (
                    <div key={opcao} className="flex items-center space-x-2">
                      <Checkbox
                        checked={field.value?.includes(opcao)}
                        onCheckedChange={(checked) => {
                          const currentValue = field.value || [];
                          const newValue = checked
                            ? [...currentValue, opcao]
                            : currentValue.filter((v: string) => v !== opcao);
                          field.onChange(newValue);
                        }}
                      />
                      <label className="text-sm font-normal cursor-pointer">
                        {opcao}
                      </label>
                    </div>
                  ))}
                </div>
                {campo.descricao && <FormDescription>{campo.descricao}</FormDescription>}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {campos.map((campo) => renderField(campo))}
    </div>
  );
};
